// Biblioteca de movimientos. Cada clip es un generador con estado: update(dt, out) escribe canales anatómicos
// (grados) en `out`. Los clips "base" escriben el cuerpo completo; los "overlay" solo lo que tocan y se
// mezclan encima con un peso (reemplazo) o se suman (additive). Todo respeta los rangos de anatomy.js.
import { GAIT, neutral } from './anatomy.js';
import { DEG, clamp, lerp, smoothstep, noise1, rand01 } from './math.js';

const TAU = Math.PI * 2;
const { sin, cos, PI } = Math;

// ---------------------------------------------------------------------------------------------------------------
// BASE: reposo
// ---------------------------------------------------------------------------------------------------------------
class Idle {
  constructor(p = {}) { this.p = { ...p }; this.t = 0; }
  set(p) { Object.assign(this.p, p); }

  update(dt, o) {
    this.t += dt;
    const t = this.t;
    const ws = noise1(t / 7, 3); // traspaso de peso lento: + carga la pierna derecha
    const rs = Math.max(0, ws);
    const ls = Math.max(0, -ws);
    const br = 0.5 - 0.5 * cos(TAU * 0.27 * t); // respiración ~16 por minuto

    o['pelvis.shift'] = 0.012 * ws;
    o['pelvis.obl'] = -2.2 * ws; // la cadera del lado cargado sube
    o['pelvis.advance'] = 0.004 * sin(TAU * 0.31 * t); // balanceo postural
    o['pelvis.tilt'] = 0.8 * sin(TAU * 0.27 * t + 0.6);
    o['L.knee.flex'] = 5 * rs; // la pierna descargada se afloja
    o['R.knee.flex'] = 5 * ls;
    o['L.hip.flex'] = 3 * rs;
    o['R.hip.flex'] = 3 * ls;
    o['L.hip.abd'] = 2 * rs;
    o['R.hip.abd'] = 2 * ls;
    o['spine.bend'] = 1.5 * ws;
    o['spine.flex'] = 1.5 - 0.8 * br;
    o['spine.rot'] = 1.2 * noise1(t / 5, 11);
    o['L.shoulder.elev'] = 1.2 * br;
    o['R.shoulder.elev'] = 1.2 * br;
    o['head.rot'] = 2 * noise1(t / 4, 5);
    o['head.flex'] = 1.5 * noise1(t / 6, 9);
    o['head.bend'] = 1.0 * noise1(t / 8, 13);
    o['gaze.x'] = 0.12 * noise1(t / 1.3, 21);
    o['gaze.y'] = 0.08 * noise1(t / 1.7, 23);
    o['face.smile'] = 0.14 + 0.05 * noise1(t / 3, 27);
    o['L.shoulder.flex'] = 3 + 1 * noise1(t / 3.1, 31);
    o['R.shoulder.flex'] = 3 + 1 * noise1(t / 3.3, 33);
    o['L.elbow.flex'] = 14 + 2 * br;
    o['R.elbow.flex'] = 14 + 2 * br;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// BASE: caminar (marcha normal en plano + rotaciones de pelvis y tronco + balanceo de brazos)
// ---------------------------------------------------------------------------------------------------------------
class Walk {
  constructor(p = {}) { this.p = { speed: 1.25, ...p }; this.phase = 0; this.t = 0; }
  set(p) { Object.assign(this.p, p); }

  get speed() { return clamp(this.p.speed, 0.3, 1.9); } // más rápido que ~1.9 m/s ya no es caminar

  /** Duración del ciclo (doble paso) en segundos: más rápido -> más cadencia. */
  get period() { return GAIT.period * (1 / this.speed) ** 0.45; }

  /** Arco de cadera necesario para que el pie de apoyo retroceda a la velocidad del cuerpo (sin patinar). */
  get hipArc() {
    const stance = this.speed * 0.62 * this.period - 0.27; // el resto lo aportan la pelvis y el balanceo del pie
    return (2 * Math.asin(clamp(stance / (2 * GAIT.legLength * 0.95), 0.05, 0.8))) / DEG;
  }

  update(dt, o) {
    this.t += dt;
    this.phase = (this.phase + dt / this.period) % 1;
    const ph = this.phase;
    const k = this.hipArc / GAIT.hipArc; // escala del paso
    const ks = clamp(k, 0.45, 1.35);
    const pr = ph; // pierna derecha: contacto de talón en la fase 0
    const pl = ph + 0.5; // la izquierda va desfasada medio ciclo

    for (const [s, phi] of [['R', pr], ['L', pl]]) {
      o[`${s}.hip.flex`] = GAIT.hipMean + (GAIT.hip(phi) - GAIT.hipMean) * k;
      o[`${s}.knee.flex`] = GAIT.knee(phi) * clamp(0.65 + 0.35 * ks, 0.7, 1.15);
      o[`${s}.ankle.pf`] = -GAIT.ankleDF(phi) * Math.sqrt(ks);
      o[`${s}.hip.abd`] = -5 * cos(TAU * (phi - 0.15)); // aducción en apoyo, abducción en balanceo
    }

    // Pelvis: rota hacia el lado de la pierna que avanza (±4°), cae del lado que no apoya (±4°), sube y baja el centro
    o['pelvis.rot'] = -4 * ks * cos(TAU * ph);
    o['pelvis.obl'] = -4 * cos(TAU * (ph - 0.15));
    o['pelvis.tilt'] = 1.2 * cos(2 * TAU * ph);
    o['pelvis.shift'] = 0.025 * cos(TAU * (ph - 0.15)); // el centro de masa se mueve sobre la pierna de apoyo

    // Tronco: contrarrota respecto de la pelvis (~9° relativos), se inclina un poco al caminar
    o['spine.rot'] = 9 * ks * cos(TAU * ph + 0.1);
    o['spine.bend'] = 0.6 * 4 * cos(TAU * (ph - 0.15));
    o['spine.flex'] = 3 + 1 * cos(2 * TAU * ph);

    // Cabeza estabilizada: compensa la rotación del tronco para mirar al frente
    const trunkYaw = o['pelvis.rot'] + o['spine.rot'];
    o['head.rot'] = -0.85 * trunkYaw;
    o['head.flex'] = -0.7 * o['spine.flex'] + 1 * cos(2 * TAU * ph + 1);
    o['head.bend'] = 0.4 * o['pelvis.obl'];
    o['gaze.x'] = 0;
    o['face.smile'] = 0.18;

    // Brazos: balanceo opuesto a la pierna del mismo lado (brazo izquierdo adelante cuando la derecha contacta)
    const sw = ks;
    o['L.shoulder.flex'] = 5 + 15 * sw * cos(TAU * ph);
    o['R.shoulder.flex'] = 5 + 15 * sw * cos(TAU * ph + PI);
    o['L.elbow.flex'] = 22 + 10 * sw * (0.5 + 0.5 * cos(TAU * ph + 0.35));
    o['R.elbow.flex'] = 22 + 10 * sw * (0.5 + 0.5 * cos(TAU * ph + PI + 0.35));
    o['L.shoulder.abd'] = 9;
    o['R.shoulder.abd'] = 9;
    o['L.shoulder.elev'] = 0;
    o['R.shoulder.elev'] = 0;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// OVERLAYS (parte alta del cuerpo y cara). Cada uno escribe solo los canales que mueve.
// ---------------------------------------------------------------------------------------------------------------
class Wave {
  constructor() { this.t = 0; }
  update(dt, o) {
    this.t += dt;
    const w = sin(TAU * 2.3 * this.t);
    o['R.shoulder.abd'] = 72 + 6 * sin(TAU * 2.3 * this.t + 1);
    o['R.shoulder.flex'] = 25;
    o['R.elbow.flex'] = 105 + 14 * w;
    o['face.smile'] = 0.75;
    o['face.brow'] = 0.25;
    o['head.bend'] = 3;
    o['head.flex'] = -2;
  }
}

/** Golpes de gesto (beat gestures) al hablar: pulsos cortos de antebrazo y cabeza sobre cada sílaba tónica. */
class TalkBeat {
  constructor(p = {}) { this.p = { seed: 1, ...p }; this.t = 0; this.k = 0; this.nextAt = 0.2; this.beatT = 9; this.hand = 'R'; }
  update(dt, o) {
    this.t += dt;
    this.beatT += dt;
    if (this.t >= this.nextAt) {
      this.k += 1;
      this.beatT = 0;
      this.hand = rand01(this.k, this.p.seed) < 0.68 ? 'R' : 'L';
      this.nextAt = this.t + 0.45 + 0.9 * rand01(this.k + 50, this.p.seed);
    }
    const b = smoothstep(this.beatT / 0.08) * (1 - smoothstep((this.beatT - 0.08) / 0.28)); // golpe rápido y retorno
    // mano que "habla" más tiempo: el reparto cambia de forma gradual, nunca de golpe
    const dR = smoothstep(noise1(this.t / 6, 41) * 2.2 + 0.5);
    for (const s of ['R', 'L']) {
      const d = s === 'R' ? dR : 1 - dR;
      const hit = this.hand === s ? b : 0;
      o[`${s}.shoulder.abd`] = lerp(12, 22, d) + 2 * noise1(this.t / 1.4, 43);
      o[`${s}.shoulder.flex`] = lerp(10, 30, d) + 6 * hit + 3 * noise1(this.t / 1.1, 45);
      o[`${s}.elbow.flex`] = lerp(38, 88, d) - 12 * hit + 4 * noise1(this.t / 1.2, 47);
    }
    o['head.flex'] = 4 * b + 1.5 * noise1(this.t / 1.5, 49);
    o['head.rot'] = 3 * noise1(this.t / 2.5, 51);
    o['face.brow'] = 0.15 + 0.5 * b;
    o['face.smile'] = 0.3;
    o['spine.flex'] = 2 + 1.5 * b;
  }
}

/** Explicar con las dos manos abiertas, con un balanceo lento. */
class TalkOpen {
  constructor() { this.t = 0; }
  update(dt, o) {
    this.t += dt;
    const t = this.t;
    for (const [s, ph] of [['L', 0], ['R', 1.7]]) {
      o[`${s}.shoulder.abd`] = 26 + 3 * sin(TAU * 0.35 * t + ph);
      o[`${s}.shoulder.flex`] = 28 + 5 * sin(TAU * 0.35 * t + ph + 1);
      o[`${s}.elbow.flex`] = 96 + 7 * sin(TAU * 0.35 * t + ph + 2);
    }
    o['head.flex'] = 1.5 * sin(TAU * 0.35 * t);
    o['face.brow'] = 0.2;
    o['face.smile'] = 0.35;
  }
}

/** Mano derecha frente al pecho: gesto de sinceridad / "yo" (sin aducción horizontal, la mano queda delante del pecho). */
class HandChest {
  constructor() { this.t = 0; }
  update(dt, o) {
    this.t += dt;
    o['R.shoulder.abd'] = 6;
    o['R.shoulder.flex'] = 24;
    o['R.elbow.flex'] = 100;
    o['head.flex'] = 3;
    o['spine.flex'] = 3;
    o['face.smile'] = 0.4;
  }
}

/** Señalar hacia adelante con el brazo derecho. */
class Point {
  constructor() { this.t = 0; }
  update(dt, o) {
    this.t += dt;
    o['R.shoulder.abd'] = 28;
    o['R.shoulder.flex'] = 80;
    o['R.elbow.flex'] = 15;
    o['head.rot'] = -4;
    o['face.smile'] = 0.4;
  }
}

class Shrug {
  constructor(p = {}) { this.p = p; this.t = 0; this.d = 1.6; }
  update(dt, o) {
    this.t += dt;
    const b = sin(PI * clamp(this.t / this.d, 0, 1)) ** 0.7;
    // Encogimiento real: hombros arriba, codos flexionados ~70° con los antebrazos hacia adelante (manos a la altura de la cintura),
    // brazos apenas separados del tronco. Con los brazos estirados y abiertos parece una T.
    for (const [s, k] of [['L', 1], ['R', 0.9]]) {
      o[`${s}.shoulder.elev`] = 11 * b * k;
      o[`${s}.elbow.flex`] = 46 * b * k; // incrementos: el overlay es aditivo sobre el reposo (codo 14°)
      o[`${s}.shoulder.abd`] = 10 * b;
      o[`${s}.shoulder.flex`] = 16 * b;
    }
    o['head.bend'] = 5 * b;
    o['head.flex'] = -2 * b;
    o['spine.flex'] = -1.5 * b;
    o['face.brow'] = 0.6 * b;
  }
}

class NodYes {
  constructor() { this.t = 0; this.d = 1.6; }
  update(dt, o) {
    this.t += dt;
    const w = sin(PI * clamp(this.t / this.d, 0, 1)) ** 0.5;
    const n = sin(PI * 1.7 * this.t) ** 2;
    o['head.flex'] = 7 * n * w;
    o['neck.flex'] = 2.5 * n * w;
  }
}

class ShakeNo {
  constructor() { this.t = 0; this.d = 1.8; }
  update(dt, o) {
    this.t += dt;
    const w = sin(PI * clamp(this.t / this.d, 0, 1)) ** 0.5;
    const n = sin(TAU * 1.8 * this.t);
    o['head.rot'] = 15 * n * w;
    o['neck.rot'] = 6 * n * w;
  }
}

/**
 * Registro de movimientos.
 *  kind: 'base' (cuerpo completo, se hace crossfade entre bases) | 'overlay' (capa encima).
 *  additive: el overlay SUMA sus valores en vez de reemplazar.
 *  duration: segundos (si no tiene y no es loop, dura hasta que se detenga).  loop: sigue hasta stop().
 */
export const CLIPS = {
  idle: { kind: 'base', label: 'Reposo', make: (p) => new Idle(p) },
  walk: { kind: 'base', label: 'Caminar', make: (p) => new Walk(p) },
  wave: { kind: 'overlay', label: 'Saludar', duration: 2.8, make: (p) => new Wave(p) },
  talk_beat: { kind: 'overlay', label: 'Hablar (golpes)', loop: true, make: (p) => new TalkBeat(p) },
  talk_open: { kind: 'overlay', label: 'Hablar (manos abiertas)', loop: true, make: (p) => new TalkOpen(p) },
  hand_chest: { kind: 'overlay', label: 'Mano al pecho', duration: 3.2, make: (p) => new HandChest(p) },
  point: { kind: 'overlay', label: 'Señalar', duration: 2.4, make: (p) => new Point(p) },
  shrug: { kind: 'overlay', label: 'Encogerse de hombros', duration: 1.6, additive: true, make: (p) => new Shrug(p) },
  nod_yes: { kind: 'overlay', label: 'Asentir', duration: 1.6, additive: true, make: (p) => new NodYes(p) },
  shake_no: { kind: 'overlay', label: 'Negar', duration: 1.8, additive: true, make: (p) => new ShakeNo(p) },
};

export { neutral, lerp };
